import { create } from "zustand";
import { useUserStore } from "./userStore";
import { fetchVendors, createVendor, type VendorDto, type CreateVendorRequest } from "../apiServices/vendorApi";

function resolveOem(): string {
  const s: any = useUserStore.getState();
  const legacy = s.currentOEM?.oemName as string | undefined;
  if (legacy && legacy.trim()) return legacy;
  const id = s.user?.oemId;
  if (id) return `Oem${id}`;
  const envDefault = (import.meta as any)?.env?.VITE_DEFAULT_OEM as string | undefined;
  return envDefault && envDefault.trim() ? envDefault : "Oem4";
}

type VendorStore = {
  items: VendorDto[];
  loading: boolean;
  error: string | null;
  hasLoadedOnce: boolean;

  loadAll: () => Promise<void>;
  addVendor: (req: CreateVendorRequest) => Promise<void>;
  clear: () => void;
};

export const useVendorStore = create<VendorStore>((set, get) => ({
  items: [],
  loading: false,
  error: null,
  hasLoadedOnce: false,

  async loadAll() {
    const { accessToken } = useUserStore.getState();
    if (!accessToken) {
      set({ items: [], error: null, hasLoadedOnce: true, loading: false });
      return;
    }
    set({ loading: true, error: null });
    try {
      const oem = resolveOem();
      const list = await fetchVendors(oem);
      set({ items: list, loading: false, hasLoadedOnce: true });
    } catch (e: any) {
      set({ items: [], loading: false, hasLoadedOnce: true, error: e?.message ?? "Failed to load vendors" });
    }
  },

  async addVendor(req) {
    set({ error: null });
    try {
      const oem = resolveOem();
      const dto = await createVendor(oem, req);
      // Prepend & keep simple alpha order by name
      const next = [dto, ...get().items].sort((a, b) =>
        (a.vendorName || "").localeCompare(b.vendorName || "", undefined, { sensitivity: "base" })
      );
      set({ items: next });
    } catch (e: any) {
      set({ error: e?.message ?? "Failed to create vendor" });
      throw e;
    }
  },

  clear: () => set({ items: [], loading: false, error: null, hasLoadedOnce: false }),
}));
